import {
  Alert,
  App,
  Checkbox,
  type CheckboxChangeEvent,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
} from "antd"
import dayjs from "dayjs"
import { motion } from "motion/react"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import styled from "styled-components"

import { classAtoms, classSessionAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Modal from "#blocks/Modal"
import { Class } from "#classes/Class"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import AddStudentReceipt from "#components/forms/AddClass/AddStudentToClass/AddStudentReceipt"
import FormWithValues from "#components/FormWithValues"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { ALL_SKILL_LEVEL_CUSTOMER_LABELS } from "#constants/skillLevelConstants"
import { useApiContext } from "#contexts/ApiContextProvider"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  AddStudentToClassReceipt,
  ENROLLMENT_RECEIPT_TYPES,
} from "#customTypes/composedTypes/emailTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { invariant } from "#customTypes/types"
import AnimatedReceiptSection from "#forms/AnimatedReceiptSection"
import SkillLevelDropdown from "#forms/SkillLevelDropdown"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import {
  useGetAllStudents,
  useGetOneStudent,
} from "#hooks/jotai/useGetStudents"
import {
  useOneDoc,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"
import useOptimizedFilter from "#hooks/newHooks/useOptimizedFilter"
import { useInvalidateCheckUsage } from "#hooks/reactQuery/useCheckUsage"
import { useFirebaseBatchUpdate } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"
import { useErrorModal } from "#hooks/useErrorModal"
import { buildAttendanceUpdate } from "#utils/classHelpers"
import {
  formatDob,
  getNow,
  isTodayOrFuture,
  STANDARD_DATE_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
  UNKNOWN_DOB,
} from "#utils/dateHelpers"
import { getStudentRecipients } from "#utils/emailHelpers"
import {
  type AnySkillLevel,
  assertEnrollmentSkillLevel,
  buildSkillLevelStudentPatch,
  formatMissingSkillLevelMessage,
  getMissingSkillLevelForEnrollment,
} from "#utils/enrollmentSkillLevelHelpers"
import {
  getBlankAttendance,
  getNewAttendanceForClass,
  getStudentOptions,
  hasRealEnrollmentHistory,
  remapAbsentForFutureSessions,
} from "#utils/studentHelpers"
import { getSkillLevelForClassGroup } from "#utils/studentSkillHelpers"
import { ErrorType } from "#utils/textFormatters"

import ClassSessionSelectionGrid from "../ClassSessionSelectionGrid"
import { useSessionSelectionState } from "../useSessionSelectionState"

const RECEIPT_ANIMATION_DURATION = 150

const SModal = styled(Modal)<{ showReceipt: boolean }>`
  transition: width 0.3s ease-out;
  width: ${({ showReceipt }) => (showReceipt ? "1200px" : "600px")};
`

const AddStudentToClassForm = ({
  sessionIds,
  onClose,
  open,
  studentId = null,
  classId,
  className,
  curriculumId,
}: {
  sessionIds: string[]
  onClose: () => void
  open: boolean
  studentId?: string | null
  classId: string
  className: string
  curriculumId?: string
}) => {
  const isEdit = !!studentId
  const { message } = App.useApp()
  const { apiHandler } = useApiContext()
  const { trackStudentEnrollmentAction } = useAnalytics()
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false)

  // Create a dedicated form for receipt-related fields
  // This ensures the receipt works regardless of parent form context
  const [receiptForm] = Form.useForm()

  const [showReceipt, setShowReceipt] = useState(false)
  const [showReceiptContent, setShowReceiptContent] = useState(false)

  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    studentId
  )
  const selectedStudent = useGetOneStudent(selectedStudentId)

  // Fetch class data directly so we don't depend on parent form context
  const classData = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classId
  )

  const stableSessionIds = useStableIds(sessionIds)
  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )
  const isClassSessionsFulfilled = useIsFulfilled(
    stableSessionIds,
    classSessions
  )

  const attendanceForClass = useMemo(() => {
    if (!stableSessionIds.length || !isClassSessionsFulfilled) return []

    const blankAttendance = getBlankAttendance(stableSessionIds)
    const attendance = selectedStudent?.attendance?.[classId]
    const now = getNow().startOf("day")

    return blankAttendance.map((session, i) => {
      const originalStatus =
        attendance?.[i]?.status &&
        attendance[i].status !== ATTENDANCE_STATUSES.INVALID
          ? attendance?.[i]?.status
          : session.status

      // Remap ABSENT to ENROLLED for future sessions
      const sessionDate = classSessions[i]?.eventStart
        ? dayjs(classSessions[i].eventStart)
        : now
      const remappedStatus = remapAbsentForFutureSessions(
        originalStatus,
        sessionDate,
        now
      )

      return {
        ...session,
        status: remappedStatus,
      }
    })
  }, [
    selectedStudent,
    classId,
    stableSessionIds,
    classSessions,
    isClassSessionsFulfilled,
  ])

  const [selectedSessionIndexes, setSelectedSessionIndexes] = useState<
    boolean[]
  >([])

  // Calculate selected session indexes
  useLayoutEffect(() => {
    if (!isClassSessionsFulfilled || !classSessions.length) return
    if (hasInitializedSelection) return

    const hasExistingEnrollment = attendanceForClass.some(
      ({ status }) =>
        status &&
        status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
        status !== ATTENDANCE_STATUSES.INVALID
    )

    if (hasExistingEnrollment) {
      // Use existing enrollment status
      setSelectedSessionIndexes(
        attendanceForClass.map(
          ({ status }) =>
            status &&
            status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
            status !== ATTENDANCE_STATUSES.INVALID
        )
      )
    } else {
      // Default to all future sessions for new enrollments
      const now = getNow().startOf("day")
      setSelectedSessionIndexes(
        classSessions.map((session) => {
          const sessionDate = dayjs(session.eventStart).startOf("day")
          return isTodayOrFuture(sessionDate, now)
        })
      )
    }
    setHasInitializedSelection(true)
  }, [
    attendanceForClass,
    isClassSessionsFulfilled,
    classSessions,
    hasInitializedSelection,
  ])

  // Reset initialization flag when student changes
  useEffect(() => {
    setHasInitializedSelection(false)
  }, [selectedStudentId])

  // Build session metadata with disabled reasons
  const sessionMetadata = useMemo(() => {
    return attendanceForClass.map((attendance) => {
      const isDisabled =
        attendance.status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
        attendance.status !== ATTENDANCE_STATUSES.ENROLLED

      return {
        disabledReason: isDisabled
          ? "Session has already been attended"
          : undefined,
      }
    })
  }, [attendanceForClass])

  const { newlySelectedSessions, droppedSessions, netSessionCount } =
    useSessionSelectionState(selectedSessionIndexes, attendanceForClass)

  // Determine if this is a renewal (student has real enrollment history) or new enrollment
  const isRenewal = useMemo(() => {
    return hasRealEnrollmentHistory(attendanceForClass)
  }, [attendanceForClass])

  // JARVIS-43: a non-camp enrollment needs a skill level in the class group.
  // Only enforced when the save leaves the student with sessions selected —
  // un-enrolling someone must never be blocked by a missing level.
  const isEnrollingSessions = selectedSessionIndexes.some(Boolean)
  const missingSkillLevel = useMemo(
    () =>
      isEnrollingSessions
        ? getMissingSkillLevelForEnrollment(selectedStudent, classData)
        : null,
    [isEnrollingSessions, selectedStudent, classData]
  )

  // Accepts pre-captured form values to avoid race condition where Firestore's
  // optimistic local update triggers re-renders that reset the form before this runs.
  const handleSendReceipt = useCallback(
    async (formSnapshot: {
      paymentAmount: string
      optOutDates: string
      siblingDiscount: boolean
      extraClassDiscount: boolean
      startDate: string
      endDate: string
      /** Level chosen at enrollment time when the student had none (JARVIS-43). */
      enrollmentSkillLevel?: AnySkillLevel
    }) => {
      if (!selectedStudent || !isClassSessionsFulfilled || !classData)
        throw new Error("Student, class, or class sessions not found")

      const recipients = getStudentRecipients(selectedStudent)

      if (!recipients.length) {
        throw new Error("No recipients found.")
      }

      const {
        paymentAmount,
        optOutDates,
        siblingDiscount,
        extraClassDiscount,
      } = formSnapshot

      if (!paymentAmount) {
        throw new Error("Payment amount is required for a receipt.")
      }

      const classGroupFromData = classData.group

      // Receipt copy only — an unlevelled student still gets the bottom rung
      // of the class group's ladder rather than a blank line.
      const studentSkill =
        formSnapshot.enrollmentSkillLevel ??
        (getSkillLevelForClassGroup(
          selectedStudent,
          classGroupFromData,
          classData.semester
        ) ||
          (classGroupFromData === CLASS_GROUPS.DIGITAL_ART
            ? "RO"
            : classGroupFromData === CLASS_GROUPS.SCULPTURE
              ? "ST"
              : "BG"))

      const classDisplayName = new Class(classData).getDisplayName()

      const receipt: AddStudentToClassReceipt = {
        recipients,
        sendTime: dayjs().format(STANDARD_DATE_TIME_FORMAT),

        studentName: selectedStudent.name,
        DOB: formatDob(selectedStudent.dob) ?? UNKNOWN_DOB,
        studentSkill: ALL_SKILL_LEVEL_CUSTOMER_LABELS[studentSkill],

        className: classDisplayName,
        numSessions: netSessionCount,
        startDate: formSnapshot.startDate,
        endDate: formSnapshot.endDate,

        optOutDatesLabel: optOutDates ? "Opt out dates" : "",
        optOutDates,
        paymentAmount: paymentAmount ?? "",
        siblingDiscount: siblingDiscount || false,
        extraClassDiscount: extraClassDiscount || false,
      }

      const receiptType = isRenewal
        ? ENROLLMENT_RECEIPT_TYPES.ENROLLMENT_RENEWAL
        : ENROLLMENT_RECEIPT_TYPES.ENROLLMENT_NEW

      await apiHandler.sendReceiptEmail(receipt, receiptType)
      message.success("Enrollment receipt sent")
    },
    [
      apiHandler,
      selectedStudent,
      netSessionCount,
      classData,
      message,
      isClassSessionsFulfilled,
      isRenewal,
    ]
  )

  const { mutate: updateClassSessionAttendance } = useFirebaseBatchUpdate([
    DOC_TYPES.CLASS_SESSIONS,
    DOC_TYPES.STUDENTS,
    DOC_TYPES.CLASSES,
  ])

  const [isPending, setIsPending] = useState(false)

  const recalculateClassUsage = useInvalidateCheckUsage(
    DOC_TYPES.CLASSES,
    classId
  )
  const openErrorModal = useErrorModal("adding student to class")

  const handleSubmit = async () => {
    setIsPending(true)
    try {
      invariant(selectedStudentId != null, "Student not found")
      invariant(selectedStudent != null, "Student not found")
      invariant(isClassSessionsFulfilled, "Sessions not found")
      invariant(classData != null, "Class not found")

      const enrollmentSkillLevel = receiptForm.getFieldValue(
        "enrollmentSkillLevel"
      ) as AnySkillLevel | undefined
      const skillLevelRequirement = isEnrollingSessions
        ? assertEnrollmentSkillLevel(
            selectedStudent,
            classData,
            enrollmentSkillLevel
          )
        : null
      const skillLevelPatch =
        skillLevelRequirement && enrollmentSkillLevel
          ? buildSkillLevelStudentPatch(
              selectedStudent,
              skillLevelRequirement,
              enrollmentSkillLevel
            )
          : {}

      const selectedSessionsWithIds = selectedSessionIndexes.map(
        (isSelected, i) => {
          return {
            isSelected,
            sessionId: classSessions[i].id,
          }
        }
      )
      const newAttendanceForClass = getNewAttendanceForClass(
        attendanceForClass,
        selectedSessionsWithIds
      )

      const {
        person,
        class: classUpdate,
        classSessionsById,
      } = buildAttendanceUpdate(
        newAttendanceForClass,
        selectedSessionsWithIds,
        classId,
        selectedStudentId,
        "student",
        selectedStudent?.attendance
      )

      // Snapshot mutable values before the DB write — Firestore's optimistic local
      // update will fire onSnapshot listeners during the await, which re-renders
      // the receipt components and resets the form to $0.
      const formSnapshot = showReceipt
        ? {
            ...receiptForm.getFieldsValue([
              "paymentAmount",
              "optOutDates",
              "siblingDiscount",
              "extraClassDiscount",
            ]),
            enrollmentSkillLevel,
            startDate: dayjs(
              classSessions[selectedSessionIndexes.indexOf(true)]?.eventStart
            ).format(STANDARD_DATE_FORMAT),
            endDate: dayjs(
              classSessions[selectedSessionIndexes.lastIndexOf(true)]
                ?.eventStart
            ).format(STANDARD_DATE_FORMAT),
          }
        : null

      await updateClassSessionAttendance({
        [DOC_TYPES.STUDENTS]: {
          [selectedStudentId]: { ...person, ...skillLevelPatch },
        },
        [DOC_TYPES.CLASSES]: {
          [classId]: classUpdate,
        },
        [DOC_TYPES.CLASS_SESSIONS]: classSessionsById,
      })

      trackStudentEnrollmentAction(ANALYTICS_EVENTS.STUDENT_ADDED_TO_CLASS, {
        studentId: selectedStudentId,
        classId: classId,
        sessionCount: selectedSessionIndexes.filter(Boolean).length,
      })
      recalculateClassUsage()

      if (showReceipt && formSnapshot) {
        await handleSendReceipt(formSnapshot)
      }

      void message.success(`${selectedStudent.name}'s attendance updated.`)

      onClose()
    } catch (error) {
      openErrorModal(
        error as ErrorType,
        `Class: ${className}\nStudent: ${selectedStudentId}`
      )
    } finally {
      setIsPending(false)
    }
  }

  const handleShowReceiptChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked

    if (isChecked) {
      setShowReceipt(true)
      setTimeout(() => {
        setShowReceiptContent(true)
      }, RECEIPT_ANIMATION_DURATION)
    } else {
      setShowReceiptContent(false)
      setTimeout(() => {
        setShowReceipt(false)
      }, RECEIPT_ANIMATION_DURATION)
    }
  }

  const students = useGetAllStudents()

  const studentOptions = useMemo(() => {
    return getStudentOptions(students)
  }, [students])

  const { filteredItems: filteredStudentOptions, onSearch } =
    useOptimizedFilter(studentOptions)

  // Loading state: wait for student data to be available
  const isStudentsLoading = isEdit
    ? !selectedStudent // When editing, wait for selected student
    : students.length === 0 // When adding, wait for students list

  const isDataLoading =
    isPending || !isClassSessionsFulfilled || isStudentsLoading

  if (!open) return null

  return (
    <motion.div
      initial={{ width: 600 }}
      animate={{
        width: showReceipt || (!showReceipt && showReceiptContent) ? 1200 : 600,
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ overflow: "hidden" }}
    >
      <SModal
        showReceipt={showReceipt}
        loading={isDataLoading}
        title={
          isEdit
            ? `Edit student in ${className}`
            : `Add student to ${className}`
        }
        onClose={onClose}
        open={open}
        width={showReceipt ? 1200 : 600}
        onOk={() => {
          // Validates receipt fields when the receipt is open and the required
          // skill-level dropdown whenever it is rendered; otherwise a no-op.
          receiptForm
            .validateFields()
            .then(() => {
              void handleSubmit()
            })
            .catch((error) => {
              console.log("❌ receipt form validation error => ", error)
            })
        }}
      >
        <FormWithValues form={receiptForm} layout="vertical">
          {/* Header */}
          <Row style={{ margin: "20px 0 0" }}>
            <Col span={12}>
              {isEdit ? (
                <Flex>
                  <ClickableStudentInfoRow student={selectedStudent} border />
                </Flex>
              ) : (
                <Select
                  showSearch
                  filterOption={() => true} // Need this for our fuzzy search to work
                  onSearch={onSearch}
                  style={{ width: 300, marginTop: 10 }}
                  value={selectedStudentId}
                  onChange={setSelectedStudentId}
                  placeholder="Select student"
                  options={
                    filteredStudentOptions as {
                      key: string
                      label: string
                      value: string
                    }[]
                  }
                  allowClear
                />
              )}
            </Col>
          </Row>
          {missingSkillLevel && selectedStudent && (
            <Row style={{ margin: "8px 0 0" }}>
              <Col span={24} data-testid="enrollment-skill-level">
                <SkillLevelDropdown
                  fieldName="enrollmentSkillLevel"
                  classGroup={missingSkillLevel.classGroup}
                  required
                />
              </Col>
            </Row>
          )}
          {missingSkillLevel && selectedStudent && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={formatMissingSkillLevelMessage(
                missingSkillLevel,
                selectedStudent.name
              )}
            />
          )}

          <Row
            gutter={40}
            style={{
              transition: "all 0.3s ease-out",
              flexWrap: "nowrap",
            }}
          >
            {/* Sessions */}
            <Col
              span={showReceipt ? 12 : 24}
              style={{ transition: "all 0.3s ease-out", maxWidth: 580 }}
            >
              <Row>
                <Col span={24}>
                  {!isClassSessionsFulfilled ? (
                    <Spin />
                  ) : selectedStudent ? (
                    <ClassSessionSelectionGrid
                      attendanceForClass={attendanceForClass}
                      classSessions={classSessions}
                      selectedSessions={selectedSessionIndexes}
                      setSelectedSessions={setSelectedSessionIndexes}
                      disabledMessage="Sessions that the student attended can not be changed."
                      headerActions={
                        <Checkbox
                          onChange={handleShowReceiptChange}
                          style={{ scale: 1.1 }}
                        >
                          Send receipt
                        </Checkbox>
                      }
                      sessionMetadata={sessionMetadata}
                      newlySelectedSessions={newlySelectedSessions}
                      droppedSessions={droppedSessions}
                    />
                  ) : (
                    <Flex col gap={10}>
                      <h2>Sessions:</h2>
                      <Flex>
                        <h2>No student selected</h2>
                      </Flex>
                    </Flex>
                  )}
                </Col>
              </Row>
            </Col>

            {/* Receipt - wrapped in its own Form for receipt-specific fields */}
            {/* Form is always mounted to avoid reset issues on toggle */}
            <Col span={12}>
              {showReceiptContent && (
                <AnimatedReceiptSection
                  width={570}
                  isOpen={showReceiptContent}
                  isPending={isPending}
                >
                  <AddStudentReceipt
                    student={selectedStudent}
                    selectedSessions={selectedSessionIndexes}
                    attendanceForClass={attendanceForClass}
                    sortedClassSessions={classSessions}
                    netSessionCount={netSessionCount}
                    curriculumId={curriculumId}
                    classData={classData}
                  />
                </AnimatedReceiptSection>
              )}
            </Col>
          </Row>
          <Form.Item name="paymentAmount" hidden>
            <Input />
          </Form.Item>
        </FormWithValues>
      </SModal>
    </motion.div>
  )
}

export default AddStudentToClassForm
